/**
 * UCM「入向解析确认」页签容器
 * 组合：人工粘贴解析面板 + 状态/方向筛选 + 待确认列表 + 详情确认抽屉
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Card, Space, Select, Button, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import UcmInboundParsePanel from './UcmInboundParsePanel';
import UcmInboundTable from './UcmInboundTable';
import UcmInboundDetailDrawer from './UcmInboundDetailDrawer';
import { ucmService } from '../../../../api/enhancedApi';
import {
    DEFAULT_PAGINATION,
    UCM_INBOUND_TABS,
    UCM_INBOUND_DIRECTION_OPTIONS,
} from '../constants/ucmConstants';

const tabByKey = (key) => UCM_INBOUND_TABS.find((t) => t.key === key) || UCM_INBOUND_TABS[0];

const memberFromPending = (pending) => ({
    id: pending.id,
    direction: pending.direction,
    status: pending.status,
    transport_mode: pending.transport_mode,
    match_status: pending.match_status,
    uld_total: pending.uld_total,
    uld_known: pending.uld_known,
    uld_unknown: pending.uld_unknown,
    surface_trip_number: pending.surface_trip_number,
    matched_flight_op_id: pending.matched_flight_op_id,
    created_at: pending.created_at,
});

const tabKeyForStatus = (status) => {
    if (status === 'confirmed') return 'confirmed';
    if (status === 'ignored') return 'ignored';
    return 'active';
};

const UcmInboundTab = () => {
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    // 状态页签：active（待处理=pending+parse_failed）/ confirmed / ignored；历史页签自带时间窗
    const [activeTab, setActiveTab] = useState('active');
    const [directionFilter, setDirectionFilter] = useState(undefined);
    // detail：抽屉展开时的"当前看哪段（pendingId）+ 整组成员（members，用于段切换器）"
    const [detail, setDetail] = useState({ visible: false, pendingId: null, members: [] });

    const loadPending = useCallback(async (override = {}) => {
        setLoading(true);
        try {
            const tab = tabByKey(activeTab);
            const params = {
                page: pagination.current,
                page_size: pagination.pageSize,
                status: tab.status,
                recent_days: tab.recentDays,   // 仅历史页签有值；待处理为 undefined（下方清掉=不设限）
                direction: directionFilter,
                ...override,
            };
            Object.keys(params).forEach((k) => {
                if (params[k] === null || params[k] === undefined || params[k] === '') delete params[k];
            });
            const resp = await ucmService.getUcmPendingList(params);
            if (resp.data) {
                setDataSource(resp.data.items || []);
                setPagination((prev) => ({
                    ...prev,
                    total: resp.data.total || 0,
                    current: override.page || prev.current,
                }));
            }
        } catch (error) {
            console.error('加载待确认列表失败:', error);
            message.error('加载待确认列表失败');
        } finally {
            setLoading(false);
        }
    }, [pagination.current, pagination.pageSize, activeTab, directionFilter]);

    useEffect(() => {
        loadPending({ page: 1 });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleTableChange = (newPagination) => {
        setPagination((prev) => ({ ...prev, current: newPagination.current, pageSize: newPagination.pageSize }));
        loadPending({ page: newPagination.current, page_size: newPagination.pageSize });
    };

    const handleTabChange = (key) => {
        setActiveTab(key);
        setPagination((prev) => ({ ...prev, current: 1 }));
        const tab = tabByKey(key);
        // 显式透传新页签的 status/recent_days：setActiveTab 异步，loadPending 闭包里还是旧 tab
        loadPending({ status: tab.status, recent_days: tab.recentDays ?? null, page: 1 });
    };

    const handleDirectionChange = (value) => {
        setDirectionFilter(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
        loadPending({ direction: value, page: 1 });
    };

    const handleRefresh = () => loadPending({ page: pagination.current });

    // 解析出新记录后都是待处理，切回「待处理」页签并刷新，免得停在历史页签看不到新结果
    const handleParsed = () => {
        setActiveTab('active');
        setPagination((prev) => ({ ...prev, current: 1 }));
        const tab = tabByKey('active');
        loadPending({ status: tab.status, recent_days: null, page: 1 });
    };

    const showPendingById = (pending) => {
        if (!pending?.id) return;
        const targetTab = tabKeyForStatus(pending.status);
        const tab = tabByKey(targetTab);
        setActiveTab(targetTab);
        setPagination((prev) => ({ ...prev, current: 1 }));
        loadPending({ status: tab.status, recent_days: tab.recentDays ?? null, page: 1 });
        setDetail({ visible: true, pendingId: pending.id, members: [memberFromPending(pending)] });
    };

    const handleParsedRecord = async (pending, { reopen = false } = {}) => {
        if (!pending?.id) return;
        if (!reopen) {
            showPendingById(pending);
            return;
        }
        try {
            const resp = await ucmService.reopenUcmPending(pending.id);
            const reopened = resp.data?.data;
            message.success(resp.data?.message || '已恢复为待处理');
            showPendingById(reopened);
        } catch (error) {
            message.error(error.response?.data?.detail || '重新处理失败');
        }
    };

    // record 是 group 结构（来自 UcmInboundTable 的 normalizeRowToGroup）；
    // 若是从方向标签点开，会附带 memberId 指定默认选中段，否则用 group.primary_pending_id
    const handleView = (record, memberId) => {
        if (!record) return;
        const members = Array.isArray(record.members) ? record.members : [];
        const pid = memberId || record.primary_pending_id || (members[0] && members[0].id) || null;
        setDetail({ visible: true, pendingId: pid, members });
    };
    const closeDetail = () => setDetail({ visible: false, pendingId: null, members: [] });

    const handleConfirm = async (record) => {
        try {
            const resp = await ucmService.confirmUcmPending(record.id);
            message.success(resp.data?.message || '确认成功，已写台账');
            handleRefresh();
        } catch (error) {
            message.error(error.response?.data?.detail || '确认失败');
        }
    };

    const handleIgnore = async (record) => {
        try {
            const resp = await ucmService.ignoreUcmPending(record.id);
            message.success(resp.data?.message || '已忽略');
            handleRefresh();
        } catch (error) {
            message.error(error.response?.data?.detail || '忽略失败');
        }
    };

    const handleReopen = async (record) => {
        try {
            const resp = await ucmService.reopenUcmPending(record.id);
            const reopened = resp.data?.data;
            message.success(resp.data?.message || '已恢复为待处理');
            showPendingById(reopened);
        } catch (error) {
            message.error(error.response?.data?.detail || '重新处理失败');
        }
    };

    const handleReopenedInDrawer = (reopened) => {
        if (!reopened?.id) return;
        setDetail((prev) => ({
            ...prev,
            members: (prev.members || []).some((member) => member.id === reopened.id)
                ? prev.members.map((member) => (
                    member.id === reopened.id ? memberFromPending(reopened) : member
                ))
                : [memberFromPending(reopened)],
        }));
        setActiveTab('active');
        setPagination((prev) => ({ ...prev, current: 1 }));
        const tab = tabByKey('active');
        loadPending({ status: tab.status, recent_days: null, page: 1 });
    };

    return (
        <div>
            <UcmInboundParsePanel
                onParsed={handleParsed}
                onViewExisting={(pending) => handleParsedRecord(pending)}
                onReopenExisting={(pending) => handleParsedRecord(pending, { reopen: true })}
            />

            <Card
                size="small"
                title="待确认记录"
                tabList={UCM_INBOUND_TABS.map((t) => ({ key: t.key, tab: t.label }))}
                activeTabKey={activeTab}
                onTabChange={handleTabChange}
                extra={
                    <Space>
                        <Select
                            allowClear
                            placeholder="方向"
                            style={{ width: 130 }}
                            value={directionFilter}
                            onChange={handleDirectionChange}
                            options={UCM_INBOUND_DIRECTION_OPTIONS}
                        />
                        <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>刷新</Button>
                    </Space>
                }
            >
                <UcmInboundTable
                    dataSource={dataSource}
                    loading={loading}
                    pagination={pagination}
                    onChange={handleTableChange}
                    onView={handleView}
                    onConfirm={handleConfirm}
                    onIgnore={handleIgnore}
                    onReopen={handleReopen}
                />
            </Card>

            <UcmInboundDetailDrawer
                visible={detail.visible}
                pendingId={detail.pendingId}
                members={detail.members}
                onClose={closeDetail}
                onChanged={handleRefresh}
                onReopened={handleReopenedInDrawer}
            />
        </div>
    );
};

export default UcmInboundTab;
